export interface AtcRadioEffectPreset {
  seed: number | null;
  target_sr: number | null;
  mono: boolean;
  mix: number;
  preserve_loudness?: boolean;
  bp_lo: number;
  bp_hi: number;
  bp_order: number;
  preemph: number;
  comp_thresh_db: number;
  comp_ratio: number;
  comp_window_ms: number;
  comp_attack_ms: number;
  comp_release_ms: number;
  makeup_db: number;
  drive: number;
  clip_lpf_hz: number;
  noise_snr_db: number;
  noise_lo: number;
  noise_hi: number;
  hiss_mod_depth: number;
  hiss_mod_rate_hz: number;
  hiss_mode: 'always' | 'gated';
  fade_depth: number;
  fade_rate_hz: number;
  dropouts: number;
  dropout_min_ms: number;
  dropout_max_ms: number;
  dropout_floor: number;
  gate_rms_window: number;
  gate_percentile: number;
  gate_mult: number;
  gate_hang_ms: number;
  squelch_tail_ms: number;
  peak: number;
  archive_mode: 'none' | 'liveatc' | string;
  archive_sr: number | null;
  archive_lowpass_hz: number;
  archive_codec: 'none' | 'mulaw' | string;
  mp3_bitrate: string | null;
}

export type AtcRadioEffectPresetInput = Partial<AtcRadioEffectPreset> | null | undefined;

export const ATC_RADIO_EFFECT_PROCESSOR_NAME = 'chill-airwaves-atc-radio-effect';
export const DEFAULT_ATC_RADIO_EFFECT_STRENGTH = 65;
export const MAX_ATC_RADIO_EFFECT_STRENGTH = 100;
export const MIN_ATC_RADIO_EFFECT_APPLIED_STRENGTH = 75;

export type AtcRadioEffectMode = 'off' | 'min' | 'mid' | 'max';

export const ATC_RADIO_EFFECT_MODE_STRENGTHS: Record<AtcRadioEffectMode, number> = {
  off: 25,
  min: 50,
  mid: 75,
  max: MAX_ATC_RADIO_EFFECT_STRENGTH,
};

export const DEFAULT_ATC_RADIO_EFFECT_PRESET: AtcRadioEffectPreset = {
  seed: 123,
  target_sr: null,
  mono: true,
  mix: 1,
  preserve_loudness: true,
  bp_lo: 676.8507225403064,
  bp_hi: 2169.303757965092,
  bp_order: 4,
  preemph: 13.76408876154688,
  comp_thresh_db: -37.57548658489231,
  comp_ratio: 28.055727533905134,
  comp_window_ms: 9.097440329655461,
  comp_attack_ms: 7.04265693133959,
  comp_release_ms: 260.3888648620384,
  makeup_db: 18.832563160590116,
  drive: 24.174224228893458,
  clip_lpf_hz: 2048.632790492632,
  noise_snr_db: 0.5080583671287151,
  noise_lo: 390.3955617537249,
  noise_hi: 2099.3243488192556,
  hiss_mod_depth: 0,
  hiss_mod_rate_hz: 0,
  hiss_mode: 'gated',
  fade_depth: 0,
  fade_rate_hz: 0,
  dropouts: 0,
  dropout_min_ms: 0,
  dropout_max_ms: 0,
  dropout_floor: 0,
  gate_rms_window: 512,
  gate_percentile: 53.37559523085622,
  gate_mult: 0.9731648758045514,
  gate_hang_ms: 368.477173590293,
  squelch_tail_ms: 0,
  peak: 0.95,
  archive_mode: 'liveatc',
  archive_sr: 22050,
  archive_lowpass_hz: 3214.4640448214886,
  archive_codec: 'mulaw',
  mp3_bitrate: '48k',
};

const clamp = (value: number, min: number, max: number): number => Math.min(max, Math.max(min, value));

export const clampAtcRadioEffectStrength = (strength: number): number => clamp(Math.round(strength), 0, MAX_ATC_RADIO_EFFECT_STRENGTH);

export const getAtcRadioEffectMode = (strength: number): AtcRadioEffectMode => {
  const clampedStrength = clampAtcRadioEffectStrength(strength);
  const modeEntries = Object.entries(ATC_RADIO_EFFECT_MODE_STRENGTHS) as Array<
    [AtcRadioEffectMode, number]
  >;

  let closestMode = modeEntries[0][0];
  let closestDistance = Math.abs(clampedStrength - modeEntries[0][1]);

  for (const [mode, presetStrength] of modeEntries.slice(1)) {
    const distance = Math.abs(clampedStrength - presetStrength);

    if (distance < closestDistance) {
      closestMode = mode;
      closestDistance = distance;
    }
  }

  return closestMode;
};

export const getAtcRadioEffectStrengthForMode = (mode: AtcRadioEffectMode): number => (
  ATC_RADIO_EFFECT_MODE_STRENGTHS[mode]
);

export const getAppliedAtcRadioEffectStrength = (strength: number): number => {
  const clampedStrength = clampAtcRadioEffectStrength(strength);
  const minimumUiStrength = ATC_RADIO_EFFECT_MODE_STRENGTHS.off;
  const effectiveStrength = Math.max(clampedStrength, minimumUiStrength);

  const minimumAppliedStrength = clamp(
    MIN_ATC_RADIO_EFFECT_APPLIED_STRENGTH,
    0,
    MAX_ATC_RADIO_EFFECT_STRENGTH,
  );

  if (minimumAppliedStrength >= MAX_ATC_RADIO_EFFECT_STRENGTH) {
    return MAX_ATC_RADIO_EFFECT_STRENGTH;
  }

  const normalizedStrength = effectiveStrength / MAX_ATC_RADIO_EFFECT_STRENGTH;
  return minimumAppliedStrength + normalizedStrength * (MAX_ATC_RADIO_EFFECT_STRENGTH - minimumAppliedStrength);
};

export const hasActiveAtcRadioEffect = (strength: number): boolean => getAppliedAtcRadioEffectStrength(strength) > 0;

export const normalizeAtcRadioEffectPreset = (
  preset: AtcRadioEffectPresetInput,
): AtcRadioEffectPreset => {
  const merged = {
    ...DEFAULT_ATC_RADIO_EFFECT_PRESET,
    ...(preset ?? {}),
  } satisfies AtcRadioEffectPreset;

  return {
    ...merged,
    mono: merged.mono !== false,
    mix: clamp(merged.mix, 0, 1),
    preserve_loudness: merged.preserve_loudness !== false,
    bp_lo: Math.max(20, merged.bp_lo),
    bp_hi: Math.max(merged.bp_lo + 50, merged.bp_hi),
    bp_order: Math.max(2, Math.round(merged.bp_order)),
    preemph: Math.max(0, merged.preemph),
    comp_ratio: Math.max(1, merged.comp_ratio),
    comp_window_ms: Math.max(1, merged.comp_window_ms),
    comp_attack_ms: Math.max(0.1, merged.comp_attack_ms),
    comp_release_ms: Math.max(1, merged.comp_release_ms),
    drive: Math.max(0.1, merged.drive),
    clip_lpf_hz: Math.max(200, merged.clip_lpf_hz),
    hiss_mode: merged.hiss_mode === 'always' ? 'always' : 'gated',
    fade_depth: clamp(merged.fade_depth, 0, 1),
    fade_rate_hz: Math.max(0, merged.fade_rate_hz),
    dropouts: Math.max(0, Math.round(merged.dropouts)),
    dropout_min_ms: Math.max(0, merged.dropout_min_ms),
    dropout_max_ms: Math.max(merged.dropout_min_ms, merged.dropout_max_ms),
    dropout_floor: clamp(merged.dropout_floor, 0, 1),
    gate_rms_window: Math.max(32, Math.round(merged.gate_rms_window)),
    gate_percentile: clamp(merged.gate_percentile, 1, 99),
    gate_mult: Math.max(0.05, merged.gate_mult),
    gate_hang_ms: Math.max(0, merged.gate_hang_ms),
    squelch_tail_ms: Math.max(0, merged.squelch_tail_ms),
    peak: clamp(merged.peak, 0.05, 1),
    archive_lowpass_hz: Math.max(200, merged.archive_lowpass_hz),
  };
};